import styled from '@emotion/styled'
import { useState } from 'react'

import { ApiAssetPriceWithHistory, ApiStrategyTemplate } from '../../../api/_legacy/generated-api'
import { Space } from '../../../components/core-v2'
import ToastNotification from '../../../components/core-v2/atoms/toast-notification/toastNotification'
import { Tabs } from '../../../components/core-v2/molecules'
import { Errors } from '../../tradebuilder-v2/util'
import { AssetDetailsTab } from '../detailed-tabs/details'
import { ExDatesTab } from '../detailed-tabs/ex-dates'
import { OverridesTab } from '../detailed-tabs/overrides'
import { PerformanceTab } from '../detailed-tabs/performance'
import { StatisticsTab } from '../detailed-tabs/statistics'
import { UsageTab } from '../detailed-tabs/usage'
import { EditAssetsActions } from '../state/edit-asset-slice'
import { mapAssetPricesToTimeseries, mapPerfMetricsToStatistics } from '../state/mappers'
import { AssetFull } from '../types'

const StyledAssetNavigation = styled.div`
  background: var(--White99);
`

const StyledAssetNavigationTabs = styled.div`
  div {
    background: var(--White99);
  }
`

const StyledAssetContents = styled.div`
  height: calc(100vh - 255px);
  overflow-y: auto;
  max-width: 1200px;
  min-width: 680px;
`

export type AssetDetailsPanelProps = {
  actions: EditAssetsActions
  assetPriceHistory?: ApiAssetPriceWithHistory
  availableAssetClasses: string[]
  availableCptyIds: string[]
  availableCurrencies: string[]
  availableStrategies: ApiStrategyTemplate[]
  canProceed: boolean
  canUserSave: boolean
  errors: Errors
  exDateNotification: string
  isNew: boolean
  onChangeStrategyPermission: (strategyId: string, val: boolean) => void
  onNotificationClose: () => void
  onSaveAsset: () => void
  onSaveExDates: (selectedDates: Date[]) => void
  saveSuccess: boolean
  selectedAsset?: AssetFull
  serverErrors?: string[]
}

export const AssetDetailsPanel = ({
  actions,
  assetPriceHistory,
  availableAssetClasses,
  availableCptyIds,
  availableCurrencies,
  availableStrategies,
  canProceed,
  canUserSave,
  errors,
  exDateNotification,
  isNew,
  onChangeStrategyPermission,
  onNotificationClose,
  onSaveAsset,
  onSaveExDates,
  saveSuccess,
  selectedAsset,
  serverErrors
}: AssetDetailsPanelProps) => {
  const [selectedTab, setSelectedTab] = useState<string>('details')
  const [selectedAddTab, setSelectedAddTab] = useState<string>('single')

  return (
    <>
      {selectedAsset && !isNew && (
        <>
          <StyledAssetNavigation>
            <Space fontSize="15px" fontWeight="700" mx={5} pt={6}>
              {selectedAsset.asset.name}
            </Space>
            <StyledAssetNavigationTabs>
              <Tabs.List className="mt-4 mx-3" spacing="md">
                <Tabs.Tab
                  isSelected={selectedTab === 'details'}
                  value="details"
                  onClick={() => setSelectedTab('details')}
                >
                  Details
                </Tabs.Tab>
                <Tabs.Tab
                  isSelected={selectedTab === 'performance'}
                  value="performance"
                  onClick={() => setSelectedTab('performance')}
                >
                  Performance
                </Tabs.Tab>
                <Tabs.Tab
                  isSelected={selectedTab === 'statistics'}
                  value="statistics"
                  onClick={() => setSelectedTab('statistics')}
                >
                  Statistics
                </Tabs.Tab>
                <Tabs.Tab
                  isSelected={selectedTab === 'usage'}
                  value="usage"
                  onClick={() => setSelectedTab('usage')}
                >
                  Usage
                </Tabs.Tab>
                <Tabs.Tab
                  isSelected={selectedTab === 'price-overrides'}
                  value="price-overrides"
                  onClick={() => setSelectedTab('price-overrides')}
                >
                  Price overrides
                </Tabs.Tab>
                <Tabs.Tab
                  isSelected={selectedTab === 'ex-dates'}
                  value="ex-dates"
                  onClick={() => setSelectedTab('ex-dates')}
                >
                  Ex-Dates
                </Tabs.Tab>
              </Tabs.List>
            </StyledAssetNavigationTabs>
          </StyledAssetNavigation>
          <StyledAssetContents>
            {selectedTab === 'details' && (
              <Tabs.Panel value="details">
                <AssetDetailsTab
                  actions={actions}
                  asset={selectedAsset.asset}
                  availableAssetClasses={availableAssetClasses}
                  availableCptyIds={availableCptyIds}
                  availableCurrencies={availableCurrencies}
                  availableStrategies={availableStrategies}
                  canProceed={canProceed}
                  canUserSave={canUserSave}
                  errors={errors}
                  isInUse={selectedAsset.assetIsInUse}
                  isNew={isNew}
                  saveSuccess={saveSuccess}
                  serverErrors={serverErrors}
                  onChangeStrategyPermission={onChangeStrategyPermission}
                  onSaveAsset={onSaveAsset}
                />
              </Tabs.Panel>
            )}
            {selectedTab === 'performance' && (
              <Tabs.Panel className="my-3" value="performance">
                <PerformanceTab
                  timeseries={mapAssetPricesToTimeseries(assetPriceHistory.allPrices)}
                />
              </Tabs.Panel>
            )}
            {selectedTab === 'statistics' && (
              <Tabs.Panel className="m-5" value="statistics">
                <StatisticsTab
                  statistics={mapPerfMetricsToStatistics(
                    [
                      selectedAsset.threeMonthPerformance,
                      selectedAsset.sixMonthPerformance,
                      selectedAsset.oneYearMonthPerformance,
                      selectedAsset.threeYearPerformance,
                      selectedAsset.fiveYearPerformance
                    ],
                    ['3M', '6M', '1Y', '3Y', '5Y']
                  )}
                />
              </Tabs.Panel>
            )}
            {selectedTab === 'usage' && (
              <Tabs.Panel value="usage">
                <UsageTab tradeUsages={selectedAsset.assetUsage} />
              </Tabs.Panel>
            )}
            {selectedTab === 'price-overrides' && (
              <Tabs.Panel value="price-overrides">
                <OverridesTab
                  currency={selectedAsset.asset.currencyIso}
                  priceHistory={assetPriceHistory.allPrices}
                />
              </Tabs.Panel>
            )}
            {selectedTab === 'ex-dates' && (
              <Tabs.Panel value="ex-dates">
                <ExDatesTab
                  errors={errors}
                  selectedAsset={selectedAsset?.asset as any}
                  onSaveExDates={onSaveExDates}
                />
              </Tabs.Panel>
            )}
          </StyledAssetContents>
          {exDateNotification && (
            <ToastNotification
              open
              description={exDateNotification}
              notificationType="success"
              title=""
              onClose={() => onNotificationClose()}
            />
          )}
        </>
      )}
      {selectedAsset && isNew && (
        <>
          <StyledAssetNavigation>
            <Space fontSize="15px" fontWeight="bold" mx={5} pt={6}>
              Add a new asset
            </Space>
            <StyledAssetNavigationTabs>
              <Tabs.List className="mt-4 mx-3" spacing="md">
                <Tabs.Tab
                  isSelected={selectedAddTab === 'single'}
                  value="single"
                  onClick={() => setSelectedAddTab('single')}
                >
                  Add asset
                </Tabs.Tab>
              </Tabs.List>
            </StyledAssetNavigationTabs>
          </StyledAssetNavigation>
          <StyledAssetContents>
            {selectedAddTab === 'single' && (
              <Tabs.Panel value="single">
                <AssetDetailsTab
                  actions={actions}
                  asset={selectedAsset.asset}
                  availableAssetClasses={availableAssetClasses}
                  availableCptyIds={availableCptyIds}
                  availableCurrencies={availableCurrencies}
                  availableStrategies={availableStrategies}
                  canProceed={canProceed}
                  canUserSave={canUserSave}
                  errors={errors}
                  isInUse={selectedAsset.assetIsInUse}
                  isNew={isNew}
                  saveSuccess={saveSuccess}
                  serverErrors={serverErrors}
                  onChangeStrategyPermission={onChangeStrategyPermission}
                  onSaveAsset={onSaveAsset}
                />
              </Tabs.Panel>
            )}
          </StyledAssetContents>
        </>
      )}
    </>
  )
}
